import React from 'react';
import DataGridDyl from '@dyl/components/DataGridDyl';
import { lowerCase, startCase } from 'lodash';
import { TestName } from '@dyl/systems/TestsManager';
import { useTitle } from '@dyl/systems/TitleManager';
import { Box, Button, Typography } from '@mui/material';
import useStyles from './styles';
import CollapsibleButtonGroup, { CollapsibleButton } from '@dyl/components/CollapsibleButtonGroup';
import useLogic from './logic';
import { Link } from 'react-router-dom';
import FilterBlock from './FilterBlock';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import withLevel from "@dyl/utils/withLevel";
import { ENVIRONMENT } from '@dyl/env';

/** @param {import('@mui/x-data-grid').GridValueFormatterParams} params */
function formatDate({ value }) {
    if (!value) return '';

    return quickDateFormat(value);
}

const getTokenId = token => token.token_id;

export default withLevel(TokenList);

function TokenList({ globalLevel }) {
    useTitle('Passwords');
    const classes = useStyles();

    const {
        isLoading,
        tokens,
        selectedIds, setSelectedIds,

        isExporting, onExportClick,
        isDisabling, onDisableClick,
        isCalculatingSpeakingResult, onCalculateSpeakingResult,
        isDownloadingJson, onDownloadJsonClick,
        isDownloadingCsv, onDownloadCsvClick,

        countFiltered, countAll,
        pageSize, setPageSize,
        page, setPage,
        sortModel, setSortModel,
        selectAll, selectNone,

        filterParams, setFilterParams,
        searchId, setSearchId,
    } = useLogic();


    /** @type {import('@mui/x-data-grid').GridColDef[]} */
    const Columns = [
        { headerName: 'Password', field: 'token_id', width: 140 },

        { headerName: 'Type', field: 'type', width: 80,
            renderCell({ value }) {
                return startCase(lowerCase(value)) || 'Unknown';
            },
        },
        { headerName: 'Status', field: 'status', width: 100,
            valueFormatter({ value }) { return startCase(value.toLowerCase()); },
        },
        { headerName: 'BP Number', field: 'candidate_bp_no', width: 120 },
        { headerName: 'Centre Number', field: 'test_centre_number', width: 120 },
        { headerName: 'Candidate Number', field: 'legacy_number', width: 120 },
        { headerName: 'Date Generated', field: 'created_at', width: 150,
            valueFormatter: formatDate,
        },
        { headerName: 'Date Redeemed', field: 'date_of_start', width: 150,
            valueFormatter: formatDate,
        },
        { headerName: 'Description', field: 'description', width: 200 },
        { headerName: 'Test ID', field: 'test_group_id', width: 160,
            renderCell({ value }) {
                return !!value && <TestName testId={value} />;
            },
        },
        { headerName: 'Speaking Results', field: '', width: 135, sortable: false,
            renderCell({ row }) {
                return (
                    <Button onClick={() => onCalculateSpeakingResult(row.user_id)} disabled={!row.user_id || isCalculatingSpeakingResult} >
                        Calculate
                    </Button>
                );
            }
        },
    ];

    return (
        <>
            <FilterBlock
                className={classes.filterLine}
                filter={filterParams}
                onFilterChange={setFilterParams}
                search={searchId}
                onSearch={setSearchId}
            />

            <Box className={classes.actionLine}>
                <Typography variant='h6'>
                    Displaying {countFiltered} of {countAll} records.
                </Typography>

                <CollapsibleButtonGroup className={classes.actionGroup} color='primary'>
                    <CollapsibleButton down='sm' component={Link} to={`/${globalLevel.label}/passwords/generate`}>
                        Generate Passwords
                    </CollapsibleButton>
                    <CollapsibleButton down='md' onClick={onExportClick} disabled={isExporting}>
                        Export Password(s)
                    </CollapsibleButton>
                    <CollapsibleButton down='lg' onClick={onDisableClick} disabled={isDisabling}>
                        Disable Password(s)
                    </CollapsibleButton>

                    <CollapsibleButton down='xl' onClick={onDownloadCsvClick} disabled={isDownloadingCsv}>
                        Export Results
                    </CollapsibleButton>

                    {/* Legacy export; keeping as dev-only just in case. */}
                    {ENVIRONMENT === 'development' && (
                        <CollapsibleButton down='xl' onClick={onDownloadJsonClick} disabled={isDownloadingJson}>
                            Export Results (JSON)
                        </CollapsibleButton>
                    )}
                </CollapsibleButtonGroup>
            </Box>

            <DataGridDyl
                columns={Columns}
                rows={tokens}
                getRowId={getTokenId}
                selectionType='checkboxes'
                loading={isLoading}
                disableColumnFilter
                paginationMode='server'
                filterMode='server'
                sortingMode='server'
                /*
                Using the controlled selection with paginationMode="server" may result in selected rows being lost when the page is changed.
                This happens because the grid cross-checks with the rows prop and only calls onSelectionModelChange with existing row IDs.
                Depending on your server-side implementation,
                when the page changes and the new value for the rows prop does not include previously selected rows,
                the grid will call onSelectionModelChange with an empty value.
                To prevent this, enable the keepNonExistentRowsSelected prop to keep the rows selected even if they do not exist.
                */
                keepNonExistentRowsSelected

                rowCount={countFiltered}
                selectionModel={selectedIds}
                onSelectionModelChange={setSelectedIds}

                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                overrideSelectAll={selectAll}
                overrideSelectNone={selectNone}

                disableColumnMenu={false}
            />
        </>
    );
}